import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchSummaryPeople, fetchSummaryPolicies } from '../../actions/SummaryActions';
import { fetchPackageFinish } from '../../actions/EnrollmentActions';
import { fetchGetDocumentBlobForEmployee } from '../../actions/DocumentActions';
import DocumentViewer from '../DocumentViewer/DocumentViewer';
import { ContentCard } from '../Cards';
import { formatCurrency } from '../../utils/CurrencyHelper';
import { FaAngleDoubleLeft, FaDownload, FaEye } from 'react-icons/fa';
import '../../styles/components.scss';
import { ApplicationRoutes } from '../../routes/ApplicationRoutes';
import { Button, Table } from 'react-bootstrap';
import { useMounted } from '../../hooks/components/useMounted';
import { EnrollmentPackageId } from '../../types/api/primaryKeys/PrimaryKey';
import { useNumberIdsFromRoute } from '../../hooks/routing/useNumberIdsFromRoute';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { PackageSummaryPerson } from '../../types/api/enrollmentPackages/PackageSummaryPerson';
import { PackageSummaryPolicy } from '../../types/api/enrollmentPackages/PackageSummaryPolicy';
import { CarrierImage } from '../Images/CarrierImage';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { DocumentDetail } from '../../types/api/documents/DocumentDetail';
import { Nullable } from '../../types/custom/Nullable';
import { getPackageDocument } from '../../apiCalls/documents/documentApiCalls';
import { Base64ToJavaScriptBlob } from '../../utils/BlobStuff';
import { formatDate } from '../../utils/ViewHelper';

interface CurrentDocument {
    fileName: string;
    blob: Blob;
}

const PackageSummary: FC = () => {
    // Local State
    const [ showPDFModal, setShowPDFModal ] = useState(false);
    const [ currentDocument, setCurrentDocument ] = useState<Nullable<CurrentDocument>>(null);

    // Mounting
    const isMounted = useMounted();

    // Routing
    const history = useHistory();
    const { packageid } = useNumberIdsFromRoute() as { packageid: EnrollmentPackageId };

    // Redux State
    const { data: finishPage } = useWolfTracsState(s => s.enrollment.finishPage);
    const { data: summaryPolicies } = useWolfTracsState(s => s.summary.summaryPolicies);
    const { data: summaryPeople } = useWolfTracsState(s => s.summary.summaryPeople);

    // Dispatch
    const dispatch = useReduxDispatch();

    // Main data retrieval
    useEffect(() => {
        dispatch(fetchPackageFinish(packageid));
        dispatch(fetchSummaryPeople(packageid));
        dispatch(fetchSummaryPolicies(packageid));
    }, [packageid]);

    // Helpers
    const onBack = () => {
        history.push(ApplicationRoutes.applicationRoot);
    };

    const getDocumentBlob = async (document: Readonly<DocumentDetail>) => {
        if (!isMounted) {
            return;
        }

        const results = await getPackageDocument(packageid, document.documentId);

        if (!isMounted) {
            return;
        }

        setShowPDFModal(true);

        const documentBlob = Base64ToJavaScriptBlob(results.blob);

        setCurrentDocument({
            fileName: document.fileName,
            blob: documentBlob
        });
    };

    const getEmployerData = (summaryPolicies: readonly PackageSummaryPolicy[]) => {
        let employerTotal = 0;
        let employeePreTaxTotal = 0;
        let employeePostTaxTotal = 0;

        summaryPolicies.forEach(p => {
            employerTotal += p.employerContribution;
            employeePreTaxTotal += p.employeePreTaxContribution;
            employeePostTaxTotal += p.employeePostTaxContribution;
        });
        
        return { employerTotal, employeePreTaxTotal, employeePostTaxTotal };
    };

    const togglePDFModal = () => {
        if (!isMounted) {
            return;
        }
        setShowPDFModal(!showPDFModal);
    };

    // Render Helpers
    const renderPeople = (people: readonly PackageSummaryPerson[]) => {
        return people.map(person => {
            return (
                <tr key={person.personId}>
                    <td>
                        {person.name}
                    </td>
                    <td>
                        {person.ssn}
                    </td>
                    <td>
                        {formatDate(person.dateOfBirth)}
                    </td>
                    <td>
                        {person.gender}
                    </td>
                    <td>
                        {person.terminateDate !== null ? 'Relationship will be termed' : null}
                    </td>
                </tr>
            );
        });
    };

    const renderVolume = (volume: number, shouldDisplayVolumeAsPlainNumber: boolean): string => {
        if (!shouldDisplayVolumeAsPlainNumber) {
            // The common case.
            return "$" + formatCurrency(volume);
        } else {
            // The rare case, PET plans only at the moment.
            return volume.toString();
        }
    };

    const renderPolicies = (policies: readonly PackageSummaryPolicy[]) => {
        return policies.map(policy => {
            return (
                <tr key={policy.policyId}>
                    <td>
                        <CarrierImage altText={policy.carrierName} carrierId={policy.carrierId} carrierLogoFileName={policy.carrierLogoFileName} />
                    </td>
                    <td>
                        {policy.planTypeName}
                        <br />
                        {policy.planName}
                    </td>
                    <td>
                        {moment(policy.effectiveDate).format("YYYY-MM-DD")}
                    </td>
                    <td>
                        {policy.selectedVolume !== null && policy.selectedVolume > 0 ? renderVolume(policy.selectedVolume, policy.shouldDisplayVolumeAsPlainNumber) : renderPolicyParticipants(policy.participants)}
                        {policy.rateTypeId === 4 && <React.Fragment> / paycheck<br />${formatCurrency(policy.annualContribution)} / annual</React.Fragment>}
                    </td>
                    <td>
                        {policy.definedContributionUsed > 0 && <div><span>Defined Contribution used:</span><br /></div>}
                        {policy.employeePreTaxContribution > 0 || policy.employeePostTaxContribution > 0 ? <div><span className="bold">You Pay:</span><br /></div> : null}
                        {policy.employerContribution > 0 && <span>Employer Pays:</span>}
                    </td>
                    <td className="text-right">
                        {policy.definedContributionUsed > 0 && <div><span>${formatCurrency(policy.definedContributionUsed)}</span><br /></div>}
                        {policy.employeePreTaxContribution > 0 || policy.employeePostTaxContribution > 0 ? <div><span className="bold">${formatCurrency(policy.employeePreTaxContribution + policy.employeePostTaxContribution)}</span><br /></div> : null}
                        {policy.employerContribution > 0 && <span>${formatCurrency(policy.employerContribution)}</span>}
                    </td>
                </tr>
            );
        });
    };

    const renderPolicyParticipants = (participants: readonly string[]) => {
        return participants.map(name => {
            return (
                <span key={`p-${name}`}>{name}<br /></span>
            );
        });
    };

    const renderPackageDocuments = (packageDocuments: readonly DocumentDetail[]) => {
        return packageDocuments.map(document => {
            return (
                <tr key={`pd-${document.documentId}`}>
                    <td>
                        {document.fileName}
                    </td>
                    <td>
                        <div className="btn-group" role="group">
                            <button type="button" className="btn btn-sm btn-outline-success" onClick={() => getDocumentBlob(document)}><FaEye /></button>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => dispatch(fetchGetDocumentBlobForEmployee(document.documentId))}><FaDownload /></button>
                        </div>
                    </td>
                </tr>
            );
        });
    };

    // Main Render Logic
    const taxData = summaryPolicies ? getEmployerData(summaryPolicies) : null;

    return (
        <React.Fragment>
            { currentDocument && <DocumentViewer toggleModal={togglePDFModal} showModal={showPDFModal} fileName={currentDocument.fileName} blob={currentDocument.blob} /> }
            <ContentCard>
                <h2>Enrollment Summary</h2><br />
                <p className="mb-4">
                    Please review the following information carefully, making sure it is all correct.
                    You can revisit anytime within your enrollment period to make changes, add or remove subscriptions, dependents or contact information.
                </p>
                <div className="table-wrapper">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>SSN</th>
                                <th>Birthdate</th>
                                <th>Gender</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryPeople && renderPeople(summaryPeople)}
                        </tbody>
                    </table>
                </div>
            </ContentCard>

            <ContentCard>
                <h2>Policy Selections</h2><br />
                <div className="table-wrapper">
                    <table className="table table-hover mb-4">
                        <thead>
                            <tr>
                                <th>Carrier</th>
                                <th>Policy</th>
                                <th>Effective Date</th>
                                <th>Participants/Volume</th>
                                <th colSpan={2}>Contributions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryPolicies && renderPolicies(summaryPolicies)}

                            {taxData &&
                                <>
                                    <tr>
                                        <td colSpan={2}></td>
                                        <td colSpan={2}>Total Deduction Per Paycheck</td>
                                        <td className="bold">Pre-Tax:<br />Post-Tax:</td>
                                        <td className="text-right bold">${formatCurrency(taxData.employeePreTaxTotal)}<br />${formatCurrency(taxData.employeePostTaxTotal)}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}></td>
                                        <td colSpan={2}>Total paid by Employer</td>
                                        <td colSpan={2} className="text-right">${formatCurrency(taxData.employerTotal)}</td>
                                    </tr>
                                </>
                            }
                        </tbody>
                    </table>
                </div>
            </ContentCard>
            <ContentCard>
                <h2>Policy Documents</h2>
                <Table>
                    <tbody>
                        {finishPage?.packageDocuments && renderPackageDocuments(finishPage.packageDocuments)}
                    </tbody>
                </Table>
            </ContentCard>
            <div className="row">
                <div className="col-md-2"><Button type="button" className="btn btn-default mr-2" onClick={onBack}><FaAngleDoubleLeft /> Back</Button></div>
            </div>
        </React.Fragment>
    );
};

export default PackageSummary;